/**
 * ChatbotFAB — Component 6 of the help-docs-panel design.
 *
 * The 56 × 56 circular chatbot launcher button. Portals into
 * `#chatbot-launcher-slot`:
 *
 * - On `/dashboard` the reserved slot owned by `dashboard-redesign`
 *   already exists, so we portal directly into it.
 * - On every other route we self-mount an identical slot at the same
 *   fixed coordinates (see {@link resolvePortalTarget}), so the FAB is
 *   present on every route with the same geometry.
 *
 * A `MutationObserver` watches `document.body` for SPA route changes
 * that add a reserved slot after mount; when that happens we tear down
 * the self-mounted fallback and re-portal into the freshly-appeared
 * reserved slot.
 *
 * Invariant (post-mount, when {@link ChatbotFABProps.visible} is not
 * `false`): exactly one element with `id="chatbot-launcher-slot"` AND
 * exactly one element with `data-chatbot-fab` exist in the DOM.
 *
 * Ownership contract:
 * - Reserved-slot geometry is owned by `.kiro/specs/dashboard-redesign/`.
 * - `ErrorId`, session-key derivation, and higher-level open/close
 *   plumbing live in the provider (Wave 2.1) — this component is a
 *   dumb portal target for a `<button>` with an `onClick` handler.
 *
 * Requirement: help-docs-panel Req 7 (portal + self-mount + observer).
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";

import {
  CHATBOT_LAUNCHER_SLOT_ID,
  SELF_MOUNTED_ATTR,
  disposeSelfMountedSlot,
  resolvePortalTarget,
} from "./portalResolver";

export interface ChatbotFABProps {
  /** Invoked when the user activates the FAB. */
  onClick(): void;
  /**
   * When explicitly `false`, the FAB is hidden — no button, no portal,
   * no self-mounted slot. Used to opt-out on marketing / logged-out
   * routes. Defaults to visible.
   */
  visible?: boolean;
}

/**
 * Renders the chatbot FAB portaled into `#chatbot-launcher-slot`.
 * Returns `null` when {@link ChatbotFABProps.visible} is `false` or
 * before the portal target has been resolved on the first mount tick.
 */
export function ChatbotFAB(props: ChatbotFABProps): JSX.Element | null {
  const { onClick, visible } = props;
  const hidden = visible === false;

  const [target, setTarget] = useState<HTMLElement | null>(null);
  /**
   * Tracks whether the currently-referenced portal target was
   * self-mounted by us (vs. the dashboard-owned reserved slot). Used
   * in the cleanup path to know whether we should dispose the DOM
   * node on unmount.
   */
  const selfMountedRef = useRef<boolean>(false);

  useEffect(() => {
    if (hidden) {
      return;
    }
    if (typeof document === "undefined") {
      // SSR-safe: portalResolver would throw here; skip entirely.
      return;
    }

    const initial = resolvePortalTarget();
    selfMountedRef.current = initial.getAttribute(SELF_MOUNTED_ATTR) === "true";
    setTarget(initial);

    const observer = new MutationObserver(() => {
      // Look at every candidate slot in the current DOM. If a reserved
      // (non-self-mounted) slot has appeared and we are not already
      // portaling into it, migrate.
      const candidates = document.querySelectorAll<HTMLElement>(
        `#${CHATBOT_LAUNCHER_SLOT_ID}`,
      );
      let reserved: HTMLElement | null = null;
      for (const el of Array.from(candidates)) {
        if (el.getAttribute(SELF_MOUNTED_ATTR) !== "true") {
          reserved = el;
          break;
        }
      }

      if (reserved !== null && reserved !== initial) {
        // A reserved slot has appeared (e.g. SPA navigated to
        // /dashboard). Re-portal into it and clean up any leftover
        // self-mounted fallback.
        selfMountedRef.current = false;
        setTarget(reserved);
        disposeSelfMountedSlot();
      }
    });

    // Watching the full subtree covers reserved slots mounted anywhere
    // beneath <body> (e.g. deep inside a dashboard layout component).
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (selfMountedRef.current) {
        disposeSelfMountedSlot();
        selfMountedRef.current = false;
      }
      setTarget(null);
    };
  }, [hidden]);

  if (hidden || target === null) {
    return null;
  }

  return createPortal(
    <button
      type="button"
      data-chatbot-fab=""
      aria-label="Open help chatbot"
      onClick={onClick}
      className="w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-700 text-white flex items-center justify-center shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
    >
      <ChatBubbleLeftRightIcon className="w-6 h-6" aria-hidden="true" />
    </button>,
    target,
  );
}
